<template>
  <v-container>
    <!-- Hero Banner -->
    <v-sheet class="hero-banner rounded-xl mb-6 pa-6 pa-md-8" dark elevation="4">
      <v-row align="center" no-gutters>
        <v-col cols="12" md="8" class="d-flex align-center">
          <v-avatar size="64" color="white" class="mr-4 elevation-3">
            <v-img v-if="school && school.logo" :src="school.logo"></v-img>
            <v-icon v-else size="34" color="primary">mdi-school</v-icon>
          </v-avatar>
          <div>
            <div class="text-h5 font-weight-bold">{{ greeting }}, {{ schoolName }}!</div>
            <div class="hero-subtitle">
              {{ school && school.motto ? school.motto : "Here's what's happening with your school today." }}
            </div>
          </div>
        </v-col>
        <v-col cols="12" md="4" class="text-md-right mt-4 mt-md-0">
          <v-chip class="ma-1" color="white" outlined label>
            <v-icon left small>mdi-calendar-month</v-icon>{{ currentSession || '—' }}
          </v-chip>
          <v-chip class="ma-1" color="white" outlined label>
            <v-icon left small>mdi-pound-box</v-icon>{{ currentTerm || '—' }}
          </v-chip>
        </v-col>
      </v-row>
    </v-sheet>

    <info-loader-2 v-if="fetchingContent"/>

    <template v-else>
      <!-- Token Balance -->
      <v-card class="token-card rounded-xl mb-6" elevation="2">
        <v-card-text class="d-flex flex-wrap align-center">
          <v-avatar size="56" :color="tokensLeft < 3 ? 'red lighten-4' : 'deep-purple lighten-5'" class="mr-4 mb-2 mb-sm-0">
            <v-icon :color="tokensLeft < 3 ? 'red' : 'deep-purple'" size="28">mdi-shield-key</v-icon>
          </v-avatar>
          <div class="mr-6 mb-2 mb-sm-0">
            <div class="text-overline grey--text">Token Balance</div>
            <div class="text-h5 font-weight-bold">
              {{ tokensLeft }} <span class="text-body-2 grey--text font-weight-regular">/ {{ totalTokens }} purchased</span>
            </div>
          </div>
          <div class="flex-grow-1 mb-2 mb-sm-0 token-progress">
            <v-progress-linear :value="tokenPercent" height="10" rounded :color="tokensLeft < 3 ? 'red' : 'success'" background-color="grey lighten-3"></v-progress-linear>
          </div>
          <v-btn color="primary" class="rounded-pill font-weight-bold ml-sm-6" elevation="2" @click="buyTokenModal = !buyTokenModal">
            <v-icon left small>mdi-cart-plus</v-icon> Buy More Tokens
          </v-btn>
        </v-card-text>
      </v-card>

      <!-- Stats -->
      <v-row>
        <v-col cols="12" sm="6" md="4" v-for="(stat, i) in stats" :key="i">
          <v-card class="stat-tile rounded-xl" elevation="1">
            <v-card-text class="d-flex align-center">
              <v-avatar size="48" :color="stat.bg" class="mr-4">
                <v-icon :color="stat.color" size="24">{{ stat.icon }}</v-icon>
              </v-avatar>
              <div>
                <div class="text-h5 font-weight-bold">{{ stat.count }}</div>
                <div class="caption text-uppercase grey--text">{{ stat.title }}</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Quick Links -->
      <v-card class="rounded-xl mt-2" outlined>
        <v-card-text>
          <div class="text-overline grey--text font-weight-bold mb-2">Quick Links</div>
          <div class="d-flex flex-wrap">
            <v-btn text color="primary" class="text-none mr-2 mb-2" href="https://myschng.tawk.help/article/how-to-use-the-myschng-students-results-portal" target="_blank">
              <v-icon left small>mdi-play-circle-outline</v-icon> Watch how the software works
            </v-btn>
            <v-btn text color="primary" class="text-none mr-2 mb-2" href="https://myschng.tawk.help/article/how-tokens-work-on-mysch-results-portal" target="_blank">
              <v-icon left small>mdi-book-open-variant</v-icon> Learn about Tokens
            </v-btn>
            <v-btn text color="deep-purple" class="text-none mb-2" @click="buyTokenModal = !buyTokenModal">
              <v-icon left small>mdi-shield-key</v-icon> Buy More Tokens
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </template>

    <buy-token-modal v-if="buyTokenModal"/>
  </v-container>
</template>

<script>
import axios from 'axios';
import InfoLoader2 from '../../components/InfoLoader2.vue';
import BuyTokenModal from '../../components/BuyTokenModal.vue';

export default {
  name: 'Dashboard',
  components: {
    InfoLoader2,
    BuyTokenModal
  },
  mounted() {
     if (!this.authToken) {
         this.$router.push({name: 'Login'})
     }
     this.sessionStats()
  },
  computed: {
    schoolName() {
      return this.school ? this.school.name : 'there';
    },
    greeting() {
      const hour = new Date().getHours();
      if (hour < 12) return 'Good morning';
      if (hour < 17) return 'Good afternoon';
      return 'Good evening';
    },
    tokenPercent() {
      return this.totalTokens > 0 ? Math.min(100, (this.tokensLeft / this.totalTokens) * 100) : 0;
    },
  },
  data: () => ({
      school: localStorage.getItem('school') ? JSON.parse(localStorage.getItem('school')) : null,
      authToken:sessionStorage.getItem('authToken') ?? null,
      stats: [],
      currentSession: null,
      currentTerm: null,
      tokensLeft: 0,
      totalTokens: 0,
      fetchingContent:true,
      buyTokenModal: false,
  }),
  metaInfo: {
      meta: [
          { name: 'viewport', content: 'width=device-width,initial-scale=1.0' }
      ]
  },
  methods: {
      sessionStats () {
          axios.get(process.env.VUE_APP_BASEURL+'/stats?school_id='+this.school.school_id,
          {
              headers:
                  {
                      'Authorization': 'Bearer '+this.authToken
                  }
          })
          .then((response) => {
                this.currentSession = response.data.current_session;
                this.currentTerm = response.data.current_term;
                this.tokensLeft = response.data.tokens_left;
                this.totalTokens = response.data.total_tokens;

                this.stats.push({
                    title:'Total Terms Created',
                    count:response.data.total_terms,
                    icon:'mdi-timetable',
                    bg:'indigo lighten-5',
                    color:'indigo',
                })
                this.stats.push({
                    title:'Total Registered Students',
                    count:response.data.total_students,
                    icon:'mdi-newspaper-variant-multiple',
                    bg:'teal lighten-5',
                    color:'teal',
                })
                this.stats.push({
                    title:'This Session Students',
                    count:response.data.this_session_students,
                    icon:'mdi-account',
                    bg:'orange lighten-5',
                    color:'orange darken-2',
                })

                localStorage.setItem('currentSession', response.data.current_session);
                localStorage.setItem('currentTerm', response.data.current_term);
                this.$store.commit("tokensLeft", response.data.tokens_left);
                this.fetchingContent = false;
          })
          .catch((error) => {
              if (error.response?.status == 401) {
                  localStorage.removeItem('authToken')
              }
              this.$toast.error(error.response ? error.response.data.message : 'APP is temporarily unavailable. Wait or Contact us!', {
                  timeout: 5000
              });
          })
          .finally(() => {
              this.fetchingContent = false;
          });
      },
  },
  }
</script>

<style scoped>
.hero-banner {
  background: linear-gradient(135deg, #00695c 0%, #1e3c72 100%);
}
.hero-subtitle {
  opacity: 0.85;
  font-size: 0.95rem;
  margin-top: 2px;
}
.token-card {
  background: linear-gradient(135deg, #ffffff 0%, #f1edff 100%);
}
.token-progress {
  min-width: 160px;
}
.stat-tile {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.stat-tile:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.08) !important;
}
.text-none {
  text-transform: none !important;
}
</style>
